import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { InkDivider } from "../components/InkDivider";
import akinLogo from "../../akin.svg";

// ─── Decorative ink dot cluster ───────────────────────────────────────────────

function InkDots() {
	return (
		<span
			className="inline-flex items-center gap-1 mr-2 shrink-0"
			aria-hidden="true"
		>
			<span className="w-[3px] h-[3px] rounded-full bg-ink/30 inline-block" />
			<span className="w-[3px] h-[3px] rounded-full bg-ink/20 inline-block" />
			<span className="w-[3px] h-[3px] rounded-full bg-ink/15 inline-block" />
		</span>
	);
}

// ─── Section eyebrow label ────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
	return (
		<p className="font-ui text-[11px] uppercase tracking-[0.15em] text-ink/40 mb-6 flex items-center">
			<InkDots />
			{children}
		</p>
	);
}

// ─── Primary CTA button with coral hover shadow ───────────────────────────────

function PrimaryButton({
	children,
	onClick,
	type = "button",
	className = "",
}: {
	children: React.ReactNode;
	onClick?: () => void;
	type?: "button" | "submit";
	className?: string;
}) {
	const [hovered, setHovered] = useState(false);
	return (
		<button
			type={type}
			onClick={onClick}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			className={`inline-flex items-center justify-center bg-ink text-cream rounded-[2px] font-ui text-[13px] uppercase tracking-[0.1em] cursor-pointer border-none px-8 py-3.5 ${className}`}
			style={{
				boxShadow: hovered ? "4px 4px 0px #E84B6A" : "none",
				transition: "box-shadow 150ms ease",
			}}
		>
			{children}
		</button>
	);
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function WelcomePage() {
	const { token } = useAuth();

	const [scrolled, setScrolled] = useState(false);
	const [email, setEmail] = useState("");
	const [submitted, setSubmitted] = useState(false);

	// Hero stagger refs
	const heroEyebrowRef = useRef<HTMLParagraphElement>(null);
	const heroH1Ref = useRef<HTMLHeadingElement>(null);
	const heroSubRef = useRef<HTMLParagraphElement>(null);
	const heroCtaRef = useRef<HTMLDivElement>(null);

	// Risograph blob refs for parallax
	const blob1Ref = useRef<HTMLDivElement>(null); // coral
	const blob2Ref = useRef<HTMLDivElement>(null); // teal
	const blob3Ref = useRef<HTMLDivElement>(null); // blue
	const blob4Ref = useRef<HTMLDivElement>(null); // violet
	const blob5Ref = useRef<HTMLDivElement>(null); // orange

	// Scroll-to targets
	const howItWorksRef = useRef<HTMLElement>(null);
	const footerFormRef = useRef<HTMLDivElement>(null);

	// Sticky nav
	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 60);
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	// Blob parallax (desktop only, respects reduced-motion)
	useEffect(() => {
		const isMobile = window.matchMedia("(max-width: 640px)").matches;
		const prefersReduced = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		if (isMobile || prefersReduced) return;

		let rafId = 0;
		let currentY = 0;
		let ticking = false;

		function update() {
			const y = currentY * 0.15;
			if (blob1Ref.current)
				blob1Ref.current.style.transform = `translateY(${y}px)`;
			if (blob2Ref.current)
				blob2Ref.current.style.transform = `translateY(${y * 0.7}px)`;
			if (blob3Ref.current)
				blob3Ref.current.style.transform = `translateY(${y * 1.2}px)`;
			if (blob4Ref.current)
				blob4Ref.current.style.transform = `translateY(${y * 0.85}px)`;
			if (blob5Ref.current)
				blob5Ref.current.style.transform = `translateY(${y * 1.05}px)`;
			ticking = false;
		}

		function onScroll() {
			currentY = window.scrollY;
			if (!ticking) {
				rafId = requestAnimationFrame(update);
				ticking = true;
			}
		}

		window.addEventListener("scroll", onScroll, { passive: true });
		return () => {
			window.removeEventListener("scroll", onScroll);
			cancelAnimationFrame(rafId);
		};
	}, []);

	// Hero load stagger
	useEffect(() => {
		const prefersReduced = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;

		const entries = [
			{ el: heroEyebrowRef.current, delay: 0 },
			{ el: heroH1Ref.current, delay: 100 },
			{ el: heroSubRef.current, delay: 220 },
			{ el: heroCtaRef.current, delay: 360 },
		];

		if (prefersReduced) {
			for (const { el } of entries) {
				if (el) {
					el.style.opacity = "1";
					el.style.transform = "none";
				}
			}
			return;
		}

		for (const { el } of entries) {
			if (el) {
				el.style.opacity = "0";
				el.style.transform = "translateY(12px)";
			}
		}

		for (const { el, delay } of entries) {
			if (!el) continue;
			const tid = setTimeout(() => {
				el.style.transition =
					"opacity 500ms ease-out, transform 500ms ease-out";
				el.style.opacity = "1";
				el.style.transform = "translateY(0)";
			}, delay);
			// Return cleanup via the outer effect cleanup
			void tid;
		}
	}, []);

	// Scroll reveal for subsequent sections
	useEffect(() => {
		const prefersReduced = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		const reveals = document.querySelectorAll<HTMLElement>(".lp-reveal");

		if (prefersReduced) {
			for (const el of reveals) el.classList.add("lp-revealed");
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						const delay = Number(
							(entry.target as HTMLElement).dataset.revealDelay ?? 0,
						);
						setTimeout(() => entry.target.classList.add("lp-revealed"), delay);
						observer.unobserve(entry.target);
					}
				}
			},
			{ threshold: 0.15 },
		);

		for (const el of reveals) observer.observe(el);
		return () => observer.disconnect();
	}, []);

	if (token) return <Navigate to="/home" replace />;

	const STEPS = [
		{
			numeral: "1.",
			title: "A prompt finds you",
			body: "Each day, a thoughtful question surfaces something worth remembering. Not a generic prompt — a real question, like one a curious friend might ask.",
		},
		{
			numeral: "2.",
			title: "You speak. Akin listens.",
			body: "Record in your own voice — a few minutes is enough. A gentle AI asks follow-up questions that draw out the details you didn't know you still remembered.",
		},
		{
			numeral: "3.",
			title: "Your story takes shape",
			body: "Akin produces a refined version — true to how you tell it, cleaned up just enough. Re-record it with intention, or keep it as is.",
		},
		{
			numeral: "4.",
			title: "A collection grows",
			body: "Over time, your archive becomes something rare: a record of your life in your own words, shaped with care, ready to pass on.",
		},
	];

	const TESTIMONIAL_CARDS = [
		{
			quote:
				"I used to think my stories weren't interesting enough to bother with. The questions Akin asks made me realise I was wrong.",
			attr: "Beta user, 41",
			accent: "#E84B6A",
		},
		{
			quote:
				"My kids are going to have something from me that I never had from my parents. That means everything.",
			attr: "Beta user, 63",
			accent: "#3ED8C3",
		},
	];

	return (
		<>
			{/* Scoped styles for reveal animations */}
			<style>{`
        .lp-reveal {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 600ms ease-out, transform 600ms ease-out;
        }
        .lp-reveal.lp-revealed {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>

			{/* ── Risograph blob layer ── */}
			<div
				className="fixed inset-0 -z-10 overflow-hidden bg-cream pointer-events-none"
				aria-hidden="true"
			>
				<svg className="absolute w-0 h-0">
					<title>Grain filter</title>
					<defs>
						<filter id="lp-grain">
							<feTurbulence
								type="fractalNoise"
								baseFrequency="0.65"
								numOctaves="3"
								stitchTiles="stitch"
							/>
							<feColorMatrix type="saturate" values="0" />
							<feBlend in="SourceGraphic" mode="multiply" />
						</filter>
					</defs>
				</svg>
				<div
					className="absolute inset-0 opacity-[0.08]"
					style={{ filter: "url(#lp-grain)" }}
				/>

				{/* Coral — upper right */}
				<div
					ref={blob1Ref}
					className="absolute -top-32 -right-32 w-[420px] h-[420px] sm:w-[700px] sm:h-[700px] rounded-full"
					style={{
						background: "radial-gradient(circle, #E84B6A 0%, transparent 70%)",
						mixBlendMode: "multiply",
						opacity: 0.4,
						filter: "blur(80px)",
					}}
				/>
				{/* Teal — mid left */}
				<div
					ref={blob2Ref}
					className="absolute top-[25%] -left-32 w-[360px] h-[360px] sm:w-[600px] sm:h-[600px] rounded-full"
					style={{
						background: "radial-gradient(circle, #3ED8C3 0%, transparent 70%)",
						mixBlendMode: "multiply",
						opacity: 0.28,
						filter: "blur(70px)",
					}}
				/>
				{/* Blue — right, lower mid */}
				<div
					ref={blob3Ref}
					className="absolute top-[50%] -right-16 w-[330px] h-[330px] sm:w-[550px] sm:h-[550px] rounded-full"
					style={{
						background: "radial-gradient(circle, #4A7DE8 0%, transparent 70%)",
						mixBlendMode: "multiply",
						opacity: 0.3,
						filter: "blur(90px)",
					}}
				/>
				{/* Violet — lower left */}
				<div
					ref={blob4Ref}
					className="absolute bottom-[25%] -left-20 w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] rounded-full"
					style={{
						background: "radial-gradient(circle, #A855F7 0%, transparent 70%)",
						mixBlendMode: "multiply",
						opacity: 0.2,
						filter: "blur(80px)",
					}}
				/>
				{/* Orange — bottom right */}
				<div
					ref={blob5Ref}
					className="absolute -bottom-32 right-[8%] w-[360px] h-[360px] sm:w-[600px] sm:h-[600px] rounded-full"
					style={{
						background: "radial-gradient(circle, #F97316 0%, transparent 70%)",
						mixBlendMode: "multiply",
						opacity: 0.3,
						filter: "blur(70px)",
					}}
				/>
			</div>

			{/* ── Navigation ── */}
			<nav
				className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 sm:px-10 py-5"
				style={{
					background: scrolled ? "rgba(245, 240, 232, 0.92)" : "transparent",
					backdropFilter: scrolled ? "blur(10px)" : "none",
					WebkitBackdropFilter: scrolled ? "blur(10px)" : "none",
					transition: "background 200ms ease, backdrop-filter 200ms ease",
				}}
			>
				<Link to="/welcome" className="no-underline">
					<img src={akinLogo} alt="akin" className="h-8 w-auto" />
				</Link>
				<Link
					to="/login"
					className="font-ui text-[11px] uppercase tracking-[0.1em] text-ink/50 hover:text-ink/80 transition-colors no-underline"
				>
					Sign in
				</Link>
			</nav>

			{/* ── Hero ── */}
			<section className="pt-[100px] pb-[80px] px-6 sm:px-10">
				<div className="max-w-[700px] mx-auto">
					<p
						ref={heroEyebrowRef}
						className="font-ui text-[11px] uppercase tracking-[0.15em] text-ink/40 mb-5 flex items-center"
					>
						<InkDots />
						Your personal memory archive
					</p>

					<h1
						ref={heroH1Ref}
						className="font-display text-[35px] sm:text-[56px] font-bold text-ink leading-[1.1] tracking-[-0.02em] mb-5"
					>
						The stories that made you deserve to be{" "}
						<span
							className="font-body"
							style={{ fontStyle: "italic", fontWeight: "normal" }}
						>
							told well.
						</span>
					</h1>

					<p
						ref={heroSubRef}
						className="font-body text-[18px] leading-[1.75] text-ink/70 max-w-[500px] mb-10"
					>
						Akin helps you capture, deepen, and preserve the moments that matter
						— in your own voice, for the people you love.
					</p>

					<div
						ref={heroCtaRef}
						className="flex flex-row flex-wrap items-center gap-6"
					>
						<PrimaryButton
							onClick={() =>
								footerFormRef.current?.scrollIntoView({
									behavior: "smooth",
								})
							}
						>
							Request early access
						</PrimaryButton>
						<a
							href="#how-it-works"
							onClick={(e) => {
								e.preventDefault();
								howItWorksRef.current?.scrollIntoView({
									behavior: "smooth",
								});
							}}
							className="font-body italic text-[16px] text-ink/50 hover:text-ink/70 transition-colors no-underline"
						>
							See how it works ↓
						</a>
					</div>

					<p className="font-ui text-[10px] uppercase tracking-[0.1em] text-ink/30 mt-5">
						Currently in private beta — invite codes rolling out gradually.
					</p>
				</div>
			</section>

			<InkDivider />

			{/* ── Pull Quote ── */}
			<section className="py-[80px] px-6 sm:px-10">
				<div className="max-w-[640px] mx-auto text-center lp-reveal">
					<blockquote className="font-body italic text-[20px] sm:text-[26px] leading-[1.6] text-ink max-w-[580px] mx-auto">
						"I recorded a story about my grandmother's kitchen that I'd been
						carrying around for thirty years. It took eleven minutes. I've
						listened to it four times."
					</blockquote>
					<p className="font-ui text-[10px] uppercase tracking-[0.15em] text-ink/40 mt-5">
						— Beta user, 54
					</p>
				</div>
			</section>

			<InkDivider />

			{/* ── How It Works ── */}
			<section
				ref={howItWorksRef}
				id="how-it-works"
				className="py-[80px] px-6 sm:px-10"
			>
				<div className="max-w-[700px] mx-auto">
					<SectionLabel>How it works</SectionLabel>

					<div className="space-y-12">
						{STEPS.map((step, i) => (
							<div
								key={step.numeral}
								className="flex flex-col sm:flex-row gap-3 sm:gap-6 lp-reveal"
								data-reveal-delay={i * 80}
							>
								<span className="font-body italic text-[14px] text-ink/40 sm:min-w-[28px] sm:pt-[5px]">
									{step.numeral}
								</span>
								<div>
									<h3 className="font-display text-[22px] font-bold text-ink mb-2">
										{step.title}
									</h3>
									<p className="font-body text-[17px] leading-[1.75] text-ink/70 max-w-[520px]">
										{step.body}
									</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<InkDivider />

			{/* ── Why It Matters ── */}
			<section className="py-[80px] px-6 sm:px-10">
				<div className="max-w-[700px] mx-auto">
					<SectionLabel>Why it matters</SectionLabel>

					<h2 className="font-display text-[32px] sm:text-[38px] font-bold text-ink leading-[1.2] mb-6 lp-reveal">
						Most stories don't get told. Not because they don't matter.
					</h2>

					<p
						className="font-body text-[18px] leading-[1.8] text-ink/70 max-w-[560px] mb-12 lp-reveal"
						data-reveal-delay={80}
					>
						We carry memories for decades — the summer that changed everything,
						the person who taught us without knowing it, the place that still
						feels like home. We mean to write them down. We never quite do. Akin
						is the quiet companion that makes it easy to finally start.
					</p>

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
						{TESTIMONIAL_CARDS.map((card, i) => (
							<div
								key={card.attr}
								className="bg-cream/80 border border-ink/20 rounded-[4px] p-6 lp-reveal"
								data-reveal-delay={i * 120}
								style={{ borderLeft: `3px solid ${card.accent}` }}
							>
								<p className="font-body italic text-[16px] leading-[1.7] text-ink">
									"{card.quote}"
								</p>
								<p className="font-ui text-[10px] uppercase tracking-[0.12em] text-ink/40 mt-3.5">
									— {card.attr}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			<InkDivider />

			{/* ── Footer CTA ── */}
			<section className="py-[100px] px-6 sm:px-10">
				<div ref={footerFormRef} className="max-w-[560px] mx-auto text-center">
					<h2 className="font-display text-[32px] sm:text-[38px] font-bold text-ink leading-[1.2] mb-4 lp-reveal">
						Your stories are worth keeping.
					</h2>
					<p
						className="font-body italic text-[20px] text-ink/50 mb-10 lp-reveal"
						data-reveal-delay={80}
					>
						Join the waitlist. We're opening access slowly and carefully.
					</p>

					<div className="lp-reveal" data-reveal-delay={160}>
						{submitted ? (
							<p className="font-body italic text-[18px] text-ink/70">
								You're on the list. We'll be in touch.
							</p>
						) : (
							<>
								<form
									onSubmit={(e) => {
										e.preventDefault();
										if (email.trim()) setSubmitted(true);
									}}
									className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center"
								>
									<input
										type="email"
										required
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										placeholder="your@email.com"
										className="font-body text-[16px] bg-cream border border-ink/40 rounded-[2px] px-[18px] py-[13px] w-full sm:w-[260px] text-ink placeholder:text-ink/30 outline-none focus:border-ink/70 transition-colors"
									/>
									<PrimaryButton type="submit">Request access</PrimaryButton>
								</form>
								<p className="font-ui text-[10px] uppercase tracking-[0.1em] text-ink/30 mt-4">
									No spam. Just an invite when your spot is ready.
								</p>
							</>
						)}
					</div>
				</div>
			</section>

			<InkDivider />

			{/* ── Footer ── */}
			<footer className="py-7 px-6 sm:px-10 flex items-center justify-between">
				<span className="font-display text-[16px] font-bold text-ink/40">
					akin
				</span>
				<nav aria-label="Footer links" className="flex items-center">
					{(["Privacy", "Terms", "Contact"] as const).map((label, i) => (
						<span key={label} className="flex items-center">
							{i > 0 && (
								<span className="font-ui text-[10px] text-ink/25 px-2">·</span>
							)}
							<a
								href="#"
								className="font-ui text-[10px] uppercase tracking-[0.1em] text-ink/40 hover:text-ink/60 transition-colors no-underline"
							>
								{label}
							</a>
						</span>
					))}
				</nav>
			</footer>
		</>
	);
}
